import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { find, upperFirst } from 'lodash';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  DialogContentText,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Box,
  FormControl,
  Checkbox,
  Alert,
  Select,
  TextField,
  Grid,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { GridColDef, GridRowSelectionModel, GridPaginationModel, GridSortModel, DataGrid } from '@mui/x-data-grid';

import { DEFAULT_EXPENSES_KEYS, DEFAULT_EXPENSES_TITLES } from 'shared/constants/expenses';
import { formatCurrencyFloat, formatCurrencyInt, formatInt } from 'shared/utils/numberFormatters';

import { ExpenseCompsFiltersTable } from './ExpenseCompsFiltersTable';
import { DEFAULT_EXPENSES_PROPERTIES, DIALOG_FILTER_TITLE, DIALOG_UNITS_TITLE } from './constants';
import {
  FilterValues,
  TableFilters,
  Options,
  Filter,
  PropertySummary,
  SubjectExpenses,
  TotalOperatingExpenses,
  ExpenseCompsResponse,
  FetchExpenseCompsParams,
} from './types';
import { ExpenseComp } from 'types/underwriting/income-approach/expenses';
import { STATES_LIST } from 'shared/constants/states';

interface Props {
  onAddExpenseComps: (comps: ExpenseComp[]) => void;
  selectedExpenseComps?: ExpenseComp[];
  handleClose: () => void;
  filters?: Filter[];
  propertySummary: PropertySummary;
  totalOperatingExpenses: TotalOperatingExpenses;
  subjectExpenses: SubjectExpenses;
  totalSubjectOperatingExpenses?: TotalOperatingExpenses;
  totalSubjectOperatingExpensesNetTaxes?: TotalOperatingExpenses;
  addedExpenseComps?: ExpenseComp[];
  onFetchExpenseComps?: (params: FetchExpenseCompsParams) => Promise<ExpenseCompsResponse>;
  isExpenseCompsDisabled?: boolean;
}

interface ExpenseCompsState {
  data: ExpenseComp[];
  loading: boolean;
  selectedExpenseComps: ExpenseComp[];
  totalCount: number;
  availableLocations: string[];
  availableStates: string[];
  error?: string;
}

const utilitiesExpense: Filter = {
  name: DEFAULT_EXPENSES_KEYS.utilities,
  searchResultName: DEFAULT_EXPENSES_KEYS.utilities,
  title: DEFAULT_EXPENSES_TITLES.utilities,
};

const ComparableExpensesUnitsModal: React.FC<Props> = observer(
  ({
    onAddExpenseComps,
    selectedExpenseComps = [],
    handleClose,
    filters = [],
    propertySummary,
    subjectExpenses,
    totalSubjectOperatingExpenses,
    totalSubjectOperatingExpensesNetTaxes,
    addedExpenseComps = [],
    onFetchExpenseComps,
    isExpenseCompsDisabled = false,
  }) => {
    const [expenseCompsState, setExpenseCompsState] = useState<ExpenseCompsState>({
      data: [],
      loading: false,
      selectedExpenseComps: [...selectedExpenseComps],
      totalCount: 0,
      availableLocations: [],
      availableStates: [],
    });

    const [isInFiltersState, setIsInFiltersState] = useState(true);
    const [isInUnitsState, setIsInUnitsState] = useState(false);
    const [filterValues, setFilterValues] = useState<FilterValues>({});
    const [tableFilters, setTableFilters] = useState<TableFilters>({
      sortField: 'address',
      sortDirection: 'desc',
      pageSize: 100,
      currentPage: 0,
      locations: [],
      state: '',
      expensePeriod: '',
      type: '',
      compCity: null,
      compState: null,
    });
    const [options, setOptions] = useState<Options>({
      expenseBasis: 'sf',
      includeRETaxes: true,
      utilitiesExpensesMode: 'brokenOut',
    });
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
      page: 0,
      pageSize: 100,
    });
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'address', sort: 'desc' }]);

    const onSearchExpenseCompsClick = useCallback(async () => {
      if (!onFetchExpenseComps) {
        setIsInFiltersState(false);
        setIsInUnitsState(true);
        return;
      }

      setExpenseCompsState((prev) => ({ ...prev, loading: true, error: undefined }));

      try {
        const checkedComps = [...addedExpenseComps, ...expenseCompsState.selectedExpenseComps];
        const params: FetchExpenseCompsParams = {
          checkedComps,
          options,
          filters: filterValues,
          tableFilters,
        };

        const response = await onFetchExpenseComps(params);

        const dataWithSelection = response.expenseComps.map((comp) => ({
          ...comp,
          isSelected: checkedComps.some((selected) => selected._id === comp._id || selected.boweryId === comp.boweryId),
        }));

        const selectedIds = dataWithSelection.filter((comp) => comp.isSelected).map((comp) => comp._id);

        setExpenseCompsState((prev) => ({
          ...prev,
          data: dataWithSelection,
          totalCount: response.totalCount,
          availableLocations: response.availableLocations,
          availableStates: response.availableStates,
          loading: false,
        }));

        setRowSelectionModel(selectedIds);
        setIsInFiltersState(false);
        setIsInUnitsState(true);
      } catch (error) {
        console.error('Failed to fetch expense comps:', error);
        setExpenseCompsState((prev) => ({
          ...prev,
          loading: false,
          error: 'Failed to load expense comparables. Please try again.',
        }));
      }
    }, [
      onFetchExpenseComps,
      filterValues,
      tableFilters,
      options,
      addedExpenseComps,
      expenseCompsState.selectedExpenseComps,
    ]);

    const onBackToFiltersClick = useCallback(() => {
      setIsInFiltersState(true);
      setIsInUnitsState(false);
    }, []);

    const onAddExistingComp = useCallback(() => {
      const compsToAdd = expenseCompsState.selectedExpenseComps.filter(
        (comp) => !addedExpenseComps.some((added) => added._id === comp._id || added.boweryId === comp.boweryId)
      );
      const mappedExpenseComps = compsToAdd.map((comp) => ({
        ...comp,
        id: comp._id,
      }));
      onAddExpenseComps(mappedExpenseComps);
      handleClose();
    }, [expenseCompsState.selectedExpenseComps, onAddExpenseComps, handleClose, addedExpenseComps]);

    const onCheckChange = useCallback((expenseComp: ExpenseComp, isChecked: boolean) => {
      const { _id: id } = expenseComp;

      setExpenseCompsState((prev) => {
        if (isChecked) {
          return {
            ...prev,
            selectedExpenseComps: [...prev.selectedExpenseComps, expenseComp],
          };
        } else {
          return {
            ...prev,
            selectedExpenseComps: prev.selectedExpenseComps.filter((comp) => comp._id !== id),
          };
        }
      });
    }, []);

    const updateOptions = useCallback((updates: Partial<Options>) => {
      setOptions((prev) => ({ ...prev, ...updates }));
    }, []);

    const updateFilterValues = useCallback((filterName: string, type: 'min' | 'max', value: number | undefined) => {
      setFilterValues((prev) => ({
        ...prev,
        [filterName]: {
          ...prev[filterName],
          [type]: value,
        },
      }));
    }, []);

    const filtersToUse = useMemo(() => {
      const result: Filter[] = [];

      switch (options.utilitiesExpensesMode) {
        case 'brokenOut':
          result.push(...DEFAULT_EXPENSES_PROPERTIES);
          break;
        case 'combinedElectricityAndFuel':
          const noElectricityAndFuel = filters.filter(
            (filter) => filter.name !== 'electricity' && filter.name !== 'fuel'
          );
          noElectricityAndFuel.push(utilitiesExpense);
          result.push(...noElectricityAndFuel);
          break;
        case 'combinedAll':
          const onlyUtilities = filters.filter(
            (filter) => filter.name !== 'electricity' && filter.name !== 'fuel' && filter.name !== 'waterAndSewer'
          );
          const alreadyHasUtilities = find(filters, (filter) => filter.name === 'utilities');
          if (!alreadyHasUtilities) {
            onlyUtilities.push(utilitiesExpense);
          }
          result.push(...onlyUtilities);
          break;
        default:
          result.push(...DEFAULT_EXPENSES_PROPERTIES);
      }

      return result;
    }, [options.utilitiesExpensesMode, filters]);

    const columns: GridColDef[] = useMemo(
      () => [
        {
          field: 'address',
          headerName: 'Address',
          width: 200,
          sortable: true,
        },
        {
          field: 'city',
          headerName: 'City',
          width: 100,
          sortable: true,
        },
        {
          field: 'state',
          headerName: 'State',
          width: 100,
          sortable: true,
        },
        {
          field: 'buildingType',
          headerName: 'Type',
          width: 120,
          sortable: true,
          valueFormatter: (value: any) => {
            return value ? upperFirst(value) : '';
          },
        },
        {
          field: 'grossBuildingArea',
          headerName: 'Gross Building Area',
          width: 120,
          sortable: true,
          type: 'number',
          valueFormatter: (value: any) => {
            return value ? formatInt(value) : '';
          },
        },
        {
          field: 'yearBuilt',
          headerName: 'Year Built',
          width: 100,
          sortable: true,
          type: 'number',
          valueFormatter: (value: any) => {
            return value ? value.toString() : '';
          },
        },
        {
          field: 'residentialUnits',
          headerName: 'Units',
          width: 100,
          sortable: true,
          type: 'number',
        },
        {
          field: 'expensePeriod',
          headerName: 'Period',
          width: 120,
          sortable: true,
        },
        {
          field: 'expenseYear',
          headerName: 'Expense Year',
          width: 120,
          sortable: true,
          type: 'number',
          valueFormatter: (value: any) => {
            return value ? value.toString() : '';
          },
        },
        {
          field: 'totalExpensesPerSF',
          headerName: '$/SF',
          width: 100,
          sortable: false,
          type: 'number',
          valueGetter: (value: any, row: any) => {
            const dataPath = options.includeRETaxes ? 'expensesTotalGross' : 'expensesTotalNet';
            return row[dataPath]?.psf || 0;
          },
          valueFormatter: (value: any) => {
            return value ? formatCurrencyFloat(value) : '';
          },
        },
        {
          field: 'totalExpensesPerUnit',
          headerName: '$/Unit',
          width: 100,
          sortable: false,
          type: 'number',
          valueGetter: (value: any, row: any) => {
            const dataPath = options.includeRETaxes ? 'expensesTotalGross' : 'expensesTotalNet';
            return row[dataPath]?.perUnit || 0;
          },
          valueFormatter: (value: any) => {
            return value ? formatCurrencyInt(value) : '';
          },
        },
      ],
      [options.includeRETaxes]
    );

    const handlePaginationModelChange = useCallback(
      async (model: GridPaginationModel) => {
        setPaginationModel(model);

        if (isInUnitsState && onFetchExpenseComps) {
          const newTableFilters = {
            ...tableFilters,
            currentPage: model.page,
            pageSize: model.pageSize,
          };

          setTableFilters(newTableFilters);

          setExpenseCompsState((prev) => ({ ...prev, loading: true }));

          try {
            const checkedComps = [...addedExpenseComps, ...expenseCompsState.selectedExpenseComps];
            const params: FetchExpenseCompsParams = {
              checkedComps,
              options,
              filters: filterValues,
              tableFilters: newTableFilters,
            };

            const response = await onFetchExpenseComps(params);

            const dataWithSelection = response.expenseComps.map((comp) => ({
              ...comp,
              isSelected: checkedComps.some(
                (selected) => selected._id === comp._id || selected.boweryId === comp.boweryId
              ),
            }));

            setExpenseCompsState((prev) => ({
              ...prev,
              data: dataWithSelection,
              totalCount: response.totalCount,
              loading: false,
            }));
          } catch (error) {
            console.error('Failed to fetch expense comps:', error);
            setExpenseCompsState((prev) => ({
              ...prev,
              loading: false,
              error: 'Failed to load expense comparables. Please try again.',
            }));
          }
        } else {
          setTableFilters((prev) => ({
            ...prev,
            currentPage: model.page,
            pageSize: model.pageSize,
          }));
        }
      },
      [
        isInUnitsState,
        onFetchExpenseComps,
        tableFilters,
        options,
        filterValues,
        addedExpenseComps,
        expenseCompsState.selectedExpenseComps,
      ]
    );

    const handleSortModelChange = useCallback(
      async (model: GridSortModel) => {
        setSortModel(model);

        if (isInUnitsState && onFetchExpenseComps && model.length > 0) {
          const { field, sort } = model[0];
          const newTableFilters = {
            ...tableFilters,
            sortField: field,
            sortDirection: sort || 'asc',
            currentPage: 0,
          };

          setTableFilters(newTableFilters);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));

          setExpenseCompsState((prev) => ({ ...prev, loading: true }));

          try {
            const checkedComps = [...addedExpenseComps, ...expenseCompsState.selectedExpenseComps];
            const params: FetchExpenseCompsParams = {
              checkedComps,
              options,
              filters: filterValues,
              tableFilters: newTableFilters,
            };

            const response = await onFetchExpenseComps(params);

            const dataWithSelection = response.expenseComps.map((comp) => ({
              ...comp,
              isSelected: checkedComps.some(
                (selected) => selected._id === comp._id || selected.boweryId === comp.boweryId
              ),
            }));

            setExpenseCompsState((prev) => ({
              ...prev,
              data: dataWithSelection,
              totalCount: response.totalCount,
              loading: false,
            }));
          } catch (error) {
            console.error('Failed to fetch expense comps:', error);
            setExpenseCompsState((prev) => ({
              ...prev,
              loading: false,
              error: 'Failed to load expense comparables. Please try again.',
            }));
          }
        } else if (model.length > 0) {
          const { field, sort } = model[0];
          setTableFilters((prev) => ({
            ...prev,
            sortField: field,
            sortDirection: sort || 'asc',
          }));
        }
      },
      [
        isInUnitsState,
        onFetchExpenseComps,
        tableFilters,
        options,
        filterValues,
        addedExpenseComps,
        expenseCompsState.selectedExpenseComps,
      ]
    );

    const handleRowSelectionModelChange = useCallback(
      (newRowSelectionModel: GridRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel);

        const selectedRows = expenseCompsState.data.filter((row) => newRowSelectionModel.includes(row._id));

        selectedRows.forEach((row) => {
          if (!expenseCompsState.selectedExpenseComps.find((comp) => comp._id === row._id)) {
            onCheckChange(row, true);
          }
        });
      },
      [expenseCompsState.data, expenseCompsState.selectedExpenseComps, onCheckChange]
    );

    useEffect(() => {
      if (isInUnitsState && onFetchExpenseComps) {
        onSearchExpenseCompsClick();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableFilters.currentPage, tableFilters.pageSize, tableFilters.sortField, tableFilters.sortDirection]);

    const dialogTitle = isInFiltersState ? DIALOG_FILTER_TITLE : DIALOG_UNITS_TITLE;
    const selectedCount = expenseCompsState.selectedExpenseComps.length;

    if (isExpenseCompsDisabled) {
      return (
        <Dialog open onClose={handleClose} aria-labelledby="simple-dialog-title">
          <DialogTitle id="simple-dialog-title">Expense Comp Search</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Search has been disabled temporarily. Please enable Knowledge World to use search.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    return (
      <Dialog
        open
        maxWidth="xl"
        fullWidth
        onClose={handleClose}
        aria-labelledby="simple-dialog-title"
        key="expense-search-modal"
      >
        <DialogTitle id="simple-dialog-title">
          {dialogTitle}
          {isInUnitsState && selectedCount > 0 && (
            <Box component="span" sx={{ ml: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
              ({selectedCount} selected)
            </Box>
          )}
        </DialogTitle>

        <DialogContent>
          <FormControl sx={{ width: '100%' }}>
            <Paper sx={{ marginTop: 1, padding: 2 }}>
              {expenseCompsState.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {expenseCompsState.error}
                </Alert>
              )}

              {isInFiltersState && (
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <FormLabel component="legend">Expense Basis</FormLabel>
                    <RadioGroup
                      row
                      value={options.expenseBasis}
                      onChange={(e) => updateOptions({ expenseBasis: e.target.value as 'sf' | 'unit' })}
                    >
                      <FormControlLabel value="sf" control={<Radio />} label="Per SF" />
                      <FormControlLabel value="unit" control={<Radio />} label="Per Unit" />
                    </RadioGroup>

                    <FormLabel component="legend" sx={{ mt: 2 }}>
                      Utilities Mode
                    </FormLabel>
                    <RadioGroup
                      value={options.utilitiesExpensesMode}
                      row
                      onChange={(e) =>
                        updateOptions({
                          utilitiesExpensesMode: e.target.value as Options['utilitiesExpensesMode'],
                        })
                      }
                    >
                      <FormControlLabel value="brokenOut" control={<Radio />} label="Broken Out" />
                      <FormControlLabel
                        value="combinedElectricityAndFuel"
                        control={<Radio />}
                        label="Combined Electricity & Fuel"
                      />
                      <FormControlLabel value="combinedAll" control={<Radio />} label="Combined All" />
                    </RadioGroup>
                    <Grid container spacing={2} sx={{ mt: 0.5, mb: 1 }}>
                      <Grid item xs={2}>
                        <TextField
                          label="City"
                          size="small"
                          value={tableFilters.compCity}
                          onChange={(e) => setTableFilters({ ...tableFilters, compCity: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl size="small">
                          <InputLabel id="state-select-label">State</InputLabel>
                          <Select
                            value={tableFilters.compState || []}
                            onChange={(e) =>
                              setTableFilters({ ...tableFilters, compState: e.target.value as string[] })
                            }
                            labelId="state-select-label"
                            label="State"
                            multiple
                            size="small"
                            sx={{ minWidth: '100px' }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 250,
                                },
                              },
                            }}
                          >
                            {STATES_LIST.map(({ label, value }) => (
                              <MenuItem key={value} value={label}>
                                {label || value}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.includeRETaxes}
                          onChange={(e) => updateOptions({ includeRETaxes: e.target.checked })}
                        />
                      }
                      label="Include Real Estate Taxes"
                    />
                  </Box>

                  <ExpenseCompsFiltersTable
                    filters={filtersToUse}
                    propertySummary={propertySummary}
                    subjectExpenses={subjectExpenses}
                    totalSubjectOperatingExpenses={
                      options.includeRETaxes ? totalSubjectOperatingExpenses : totalSubjectOperatingExpensesNetTaxes
                    }
                    expenseBasis={options.expenseBasis}
                    includeRETaxes={options.includeRETaxes}
                    utilitiesExpensesMode={options.utilitiesExpensesMode}
                    filterValues={filterValues}
                    onExpenseBasisChange={(value) => updateOptions({ expenseBasis: value })}
                    onIncludeRETaxesChange={(value) => updateOptions({ includeRETaxes: value })}
                    onUtilitiesExpensesModeChange={(value) =>
                      updateOptions({
                        utilitiesExpensesMode: value as Options['utilitiesExpensesMode'],
                      })
                    }
                    onFilterValueChange={updateFilterValues}
                  />
                </Box>
              )}

              {isInUnitsState && (
                <Box sx={{ height: 600, width: '100%' }}>
                  <DataGrid
                    rows={expenseCompsState.data}
                    columns={columns}
                    loading={expenseCompsState.loading}
                    checkboxSelection
                    paginationMode="server"
                    sortingMode="server"
                    rowCount={expenseCompsState.totalCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={handleRowSelectionModelChange}
                    getRowId={(row) => row._id}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    sx={{
                      '& .MuiDataGrid-row:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    disableColumnResize
                    disableDensitySelector
                  />
                </Box>
              )}
            </Paper>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          {isInFiltersState && (
            <Button onClick={onSearchExpenseCompsClick} variant="contained" disabled={expenseCompsState.loading}>
              {expenseCompsState.loading ? 'Searching...' : 'Find Comparables'}
            </Button>
          )}
          {isInUnitsState && (
            <>
              <Button onClick={onBackToFiltersClick} variant="outlined">
                Back To Filters
              </Button>
              <Button onClick={onAddExistingComp} variant="contained" disabled={selectedCount === 0}>
                Add Comps ({selectedCount})
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);

export default ComparableExpensesUnitsModal;
